import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface LanguageSelectorProps {
  isScrolled: boolean;
  isMobile?: boolean;
}

function CombinedSelector({ isScrolled, isMobile = false }: LanguageSelectorProps) {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'fa', name: 'فارسی', flag: '🇮🇷' },
    { code: 'ru', name: 'Русский', flag: '🇷🇺' },
    { code: 'hy', name: 'Հայերեն', flag: '🇦🇲' }
  ];

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageChange = (code: string) => {
    i18n.changeLanguage(code);
    setIsOpen(false);
  };

  if (isMobile) {
    return (
      <div className="space-y-2">
        <p className="text-sm font-semibold text-gray-600 mb-3">Language</p>
        <div className="grid grid-cols-2 gap-2">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code)}
              className={`flex items-center gap-2 px-4 py-3 rounded-xl font-semibold transition-all duration-300 cursor-pointer ${
                currentLanguage.code === lang.code
                  ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-emerald-50'
              }`}
            >
              <span className="text-xl">{lang.flag}</span>
              <span className="text-sm">{lang.name}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-3 xl:px-4 py-2 rounded-xl font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap ${
          isScrolled
            ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white hover:from-emerald-700 hover:to-teal-700 shadow-lg'
            : 'bg-white text-gray-800 hover:bg-gray-50 shadow-md'
        }`}
        aria-label="Select Language"
      >
        <span className="text-lg">{currentLanguage.flag}</span>
        <span className="hidden xl:inline">{currentLanguage.name}</span>
        <i className={`ri-arrow-down-s-line text-lg transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}></i>
      </button>

      {isOpen && (
        <div className="absolute top-full right-0 mt-2 w-48 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden z-50 animate-scale-in">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code)}
              className={`w-full flex items-center gap-3 px-4 py-3 transition-all duration-300 cursor-pointer ${
                currentLanguage.code === lang.code
                  ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white'
                  : 'text-gray-700 hover:bg-emerald-50'
              }`}
            >
              <span className="text-xl">{lang.flag}</span>
              <span className="font-semibold">{lang.name}</span>
              {currentLanguage.code === lang.code && (
                <i className="ri-check-line text-lg ml-auto"></i>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default CombinedSelector;
